import { useFieldErrors } from '../../hooks/useFieldErrors';
import { useState } from 'react';
import {
  Text, Spinner, Button, Field, Input,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent, tokens,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCustomer, type Customer, type CreateCustomerInput } from '../../api/sales.api';
import { NumberInput } from '../shared/NumberInput';
import { useResetOnOpen } from '../../hooks/useResetOnOpen';
import { useT } from '../../i18n';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (customer: Customer) => void;
}

export function NewCustomerDialog({ open, onClose, onCreated }: Props) {
  const t = useT();
  const s2 = t.sales;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [termsDays, setTermsDays] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setName(''); setCountry(''); setContactName(''); setContactEmail('');
    setContactPhone(''); setTermsDays(''); setError('');
  };
  useResetOnOpen(open, reset);

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateCustomerInput = {
        name: name.trim(),
        country: country.trim() || undefined,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        paymentTermsDays: termsDays !== '' ? Number(termsDays) : undefined,
      };
      return createCustomer(input);
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      reset();
      onCreated?.(created);
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? s2.customerCreateError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      name: !name.trim() && t.common.validation.nameRequired,
      email: !!contactEmail.trim() && !EMAIL_RE.test(contactEmail.trim()) && s2.customerInvalidEmail,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{s2.newCustomerTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Field label={s2.customerName} required {...fe.props('name')}>
                <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} />
              </Field>
              <Field label={s2.customerCountry}>
                <Input value={country} onChange={(_, d) => setCountry(d.value)} />
              </Field>
              <Field label={s2.customerContact}>
                <Input value={contactName} onChange={(_, d) => setContactName(d.value)} />
              </Field>
              <Field label={s2.customerEmail} {...fe.props('email')}>
                <Input type="email" value={contactEmail} onChange={(_, d) => { setContactEmail(d.value); fe.clear('email'); }} />
              </Field>
              <Field label={s2.customerPhone}>
                <Input type="tel" value={contactPhone} onChange={(_, d) => setContactPhone(d.value)} />
              </Field>
              <Field label={s2.customerTermsDays}>
                <NumberInput value={termsDays} onValueChange={(v) => setTermsDays(v)} maxDecimals={0} />
              </Field>
              {error && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={mut.isPending}>
              {mut.isPending ? <Spinner size="tiny" /> : t.common.save}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
